// src/components/Lms/Pages/CoursePlayerPreview.jsx
// Copia de CoursePlayerStudent para vista previa de instructor
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
    FiX,
    FiChevronLeft,
    FiChevronRight,
    FiCheckCircle,
    FiPlayCircle,
    FiHelpCircle,
    FiClipboard,
    FiMessageSquare,
} from "react-icons/fi";
import { API_BASE } from "../../../api";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import "../Styles/CoursePlayer.css";
import ObservacionesModal from "../Modals/ObservacionesModal";

const formatUrl = (url) => {
    if (!url) return '';

    // YouTube
    const youtubeRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const ytMatch = url.match(youtubeRegExp);
    if (ytMatch && ytMatch[2].length === 11) {
        return `https://www.youtube.com/embed/${ytMatch[2]}`;
    }

    // Vimeo
    const vimeoRegExp = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const vimeoMatch = url.match(vimeoRegExp);
    if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return url;
};

const CoursePlayerPreview = () => {
    const { slug, lessonId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

    const [course, setCourse] = useState(null);
    const [currentLessonId, setCurrentLessonId] = useState(null);
    const [justCompletedTitle, setJustCompletedTitle] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [lessonSections, setLessonSections] = useState([]);
    const [loadingLessonContent, setLoadingLessonContent] = useState(false);
    const [sidebarModules, setSidebarModules] = useState([]);

    const [isObservacionesModalOpen, setIsObservacionesModalOpen] = useState(false);

    // Estados para contenido actual (lección, cuestionario o examen)
    const [currentContentType, setCurrentContentType] = useState(null); // "LECCION", "CUESTIONARIO", "EXAMEN"
    const [currentContentId, setCurrentContentId] = useState(null);
    const [currentContentTitle, setCurrentContentTitle] = useState("");

    // Estados para cuestionarios y exámenes
    const [quizPreguntas, setQuizPreguntas] = useState([]);
    const [examPreguntas, setExamPreguntas] = useState([]);
    const [respuestasUsuario, setRespuestasUsuario] = useState({}); // { idPregunta: idOpcionSeleccionada }
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [quizSubmitted, setQuizSubmitted] = useState(false);
    const [quizResult, setQuizResult] = useState(null); // { total, contestadas, correctas, incorrectas }
    const [examSubmitted, setExamSubmitted] = useState(false);
    const [examResult, setExamResult] = useState(null);

    // Modal de resultados
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [resultsData, setResultsData] = useState({
        correctas: 0,
        incorrectas: 0,
        calificacion: 0,
        aprobado: false
    });

    // Cargar estructura del curso (módulos + todo el contenido) desde BD
    useEffect(() => {
        const loadCourseStructure = async () => {
            const courseId = Number(slug);
            if (!courseId) {
                setCourse(null);
                setSidebarModules([]);
                setCurrentLessonId(null);
                return;
            }

            try {
                // 1. Cargar curso por ID
                let cursoEncontrado = null;
                try {
                    const resCurso = await axios.get(LMS_ENDPOINTS.cursosById(courseId));
                    if (resCurso.data) {
                        // Normalizamos si es array (algunos endpoints retornan lista) o objeto
                        const rawOpt = Array.isArray(resCurso.data) ? resCurso.data[0] : resCurso.data;
                        // *Nota: cursosById suele retornar objeto único, pero prevenimos error
                        // Si el endpoint retorna 404, salta al catch
                        if (rawOpt) cursoEncontrado = rawOpt;
                    }
                } catch (e) {
                    console.warn("No se encontró curso por ID:", e);
                }

                if (!cursoEncontrado) {
                    setCourse(null);
                    return;
                }
                setCourse(cursoEncontrado);

                // 2. Cargar componentes filtrados por curso en paralelo
                const [modulosRes, leccionesRes, cuestionariosRes, examenesRes, moduloContenidoRes] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.moduloByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.leccionesByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.cuestionarioByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.examenByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.moduloContenidoByCurso(courseId)),
                ]);

                const modulosData = modulosRes.data || [];
                const leccionesData = leccionesRes.data || [];
                const cuestionariosData = cuestionariosRes.data || [];
                const examenesData = examenesRes.data || [];
                const moduloContenidoData = (moduloContenidoRes.data || []).map(Models.moduloContenido);

                const modulosDelCurso = modulosData.sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                const builtModules = modulosDelCurso.map((m) => {
                    const idModulo = m.idModulo || m.IdModulo;
                    const tituloModulo = m.nombre || m.Nombre || "Módulo";

                    const contenidosDelModulo = moduloContenidoData
                        .filter((mc) => mc.idModulo === idModulo && mc.activo)
                        .sort((a, b) => (a.orden || 0) - (b.orden || 0));

                    const contenidoMapeado = contenidosDelModulo
                        .map((mc) => {
                            const tipo = mc.tablaContenido;

                            if (tipo === "Lecciones") {
                                const leccion = leccionesData.find(
                                    (l) => (l.idLeccion || l.IdLeccion) === mc.idAsociado
                                );
                                if (!leccion) return null;

                                return {
                                    tipo: "LECCION",
                                    id: leccion.idLeccion || leccion.IdLeccion,
                                    lessonId: leccion.idLeccion || leccion.IdLeccion,
                                    lessonTitle: leccion.titulo || leccion.Titulo || "Lección",
                                    moduleId: idModulo,
                                    completado: false,
                                };
                            }

                            if (tipo === "Cuestionario") {
                                const cuestionario = cuestionariosData.find(
                                    (q) => (q.idCuestionario || q.IdCuestionario) === mc.idAsociado
                                );
                                if (!cuestionario) return null;

                                return {
                                    tipo: "CUESTIONARIO",
                                    id: cuestionario.idCuestionario || cuestionario.IdCuestionario,
                                    quizId: cuestionario.idCuestionario || cuestionario.IdCuestionario,
                                    lessonTitle: cuestionario.nombre || cuestionario.Nombre || "Cuestionario",
                                    moduleId: idModulo,
                                };
                            }

                            if (tipo === "Examen") {
                                const examen = examenesData.find(
                                    (e) => (e.idExamen || e.IdExamen) === mc.idAsociado
                                );
                                if (!examen) return null;

                                return {
                                    tipo: "EXAMEN",
                                    id: examen.idExamen || examen.IdExamen,
                                    examId: examen.idExamen || examen.IdExamen,
                                    lessonTitle: examen.nombre || examen.Nombre || "Examen",
                                    moduleId: idModulo,
                                };
                            }

                            return null;
                        })
                        .filter(Boolean);

                    return {
                        moduleId: idModulo,
                        moduleTitle: tituloModulo,
                        items: contenidoMapeado,
                    };
                });

                setSidebarModules(builtModules);

                let initialLessonId = lessonId || null;
                if (!initialLessonId) {
                    const firstModule = builtModules.find(
                        (m) => (m.items || []).some((item) => item.tipo === "LECCION")
                    );
                    if (firstModule) {
                        const firstLesson = firstModule.items.find((item) => item.tipo === "LECCION");
                        if (firstLesson) {
                            initialLessonId = firstLesson.lessonId;
                        }
                    }
                }
                setCurrentLessonId(initialLessonId);
            } catch (error) {
                console.error("[CoursePlayerPreview] Error al cargar estructura de curso:", error);
                setCourse(null);
                setSidebarModules([]);
                setCurrentLessonId(null);
            }
        };

        loadCourseStructure();
    }, [slug]); // Se elimina lessonId para evitar recargar estructura al cambiar de lección

    // Detectar el parámetro type en la URL y cargar el contenido correspondiente
    // El parámetro type es OBLIGATORIO para cuestionarios y exámenes
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const type = searchParams.get('type');

        console.log('[CoursePlayerPreview] useEffect ejecutado - lessonId:', lessonId, 'type:', type);

        // Si no hay lessonId, no hacer nada
        if (!lessonId) return;

        if (type === 'quiz') {
            // Cargar cuestionario
            console.log('[CoursePlayerPreview] Cargando cuestionario con ID:', lessonId);
            setCurrentContentType('CUESTIONARIO');
            setCurrentContentId(lessonId);
            setCurrentContentTitle(''); // Se llenará al cargar
            setCurrentLessonId(null);
            setLessonSections([]);
            setRespuestasUsuario({});
            setQuizSubmitted(false);
            setQuizResult(null);
            loadQuizQuestions(lessonId);

        } else if (type === 'exam') {
            // Cargar examen
            console.log('[CoursePlayerPreview] Cargando examen con ID:', lessonId);
            setCurrentContentType('EXAMEN');
            setCurrentContentId(lessonId);
            setCurrentContentTitle(''); // Se llenará al cargar
            setCurrentLessonId(null);
            setLessonSections([]);
            setRespuestasUsuario({});
            setExamSubmitted(false);
            setExamResult(null);
            loadExamQuestions(lessonId);

        } else if (!type) {
            // Si no hay parámetro type, es una lección normal
            console.log('[CoursePlayerPreview] Cargando lección con ID:', lessonId);
            setCurrentContentType('LECCION');
            setCurrentContentId(lessonId);
            setCurrentLessonId(lessonId);
        }
        // Si type tiene otro valor que no sea 'quiz', 'exam' o undefined, se ignora

    }, [lessonId, location.search]);

    // Función para cargar preguntas de un cuestionario
    const loadQuizQuestions = async (cuestionarioId) => {
        console.log('[loadQuizQuestions] Iniciando carga para cuestionario:', cuestionarioId);
        setLoadingQuestions(true);

        try {
            // 1. Obtener preguntas filtradas por cuestionario (Optimizado)
            const resPreguntas = await axios.get(LMS_ENDPOINTS.cuestionariosPreguntasByCuestionario(cuestionarioId));
            const preguntasDelCuestionario = resPreguntas.data || []; // Ya vienen filtradas del servidor

            // 2. Obtener datos del cuestionario (Optimizado por ID)
            let cuestionario = null;
            try {
                const resCuestionario = await axios.get(LMS_ENDPOINTS.cuestionarioById(cuestionarioId));
                // Verificamos si devuelve lista o objeto
                const dataRaw = Array.isArray(resCuestionario.data) ? resCuestionario.data[0] : resCuestionario.data;
                if (dataRaw) cuestionario = dataRaw;
            } catch (e) {
                console.warn("No se pudo cargar datos del cuestionario:", e);
            }

            // Lógica de aleatoriedad
            let preguntasFinales = [...preguntasDelCuestionario]; // Clonar para evitar mutaciones indeseadas

            console.log(`[loadQuizQuestions] Buscando configuración para Cuestionario ID: ${cuestionarioId}. Encontrado: ${!!cuestionario}`);

            if (cuestionario) {
                const cantidadAleatoria = cuestionario.preguntasAleatorias || cuestionario.PreguntasAleatorias || 0;
                console.log(`[loadQuizQuestions] Configuración aleatoriedad: ${cantidadAleatoria}`);

                if (cantidadAleatoria > 0) {
                    console.log(`[loadQuizQuestions] Aplicando mezcla y corte...`);

                    // Mezclar las preguntas (Fisher-Yates Shuffle)
                    for (let i = preguntasFinales.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [preguntasFinales[i], preguntasFinales[j]] = [preguntasFinales[j], preguntasFinales[i]];
                    }

                    // Tomar las primeras N (o todas si N >= length)
                    const cantidadATomar = Math.min(cantidadAleatoria, preguntasFinales.length);
                    preguntasFinales = preguntasFinales.slice(0, cantidadATomar);
                    console.log(`[loadQuizQuestions] Resultado: ${preguntasFinales.length} preguntas.`);
                } else {
                    console.log('[loadQuizQuestions] Sin aleatoriedad configurada (=0).');
                }
            } else {
                console.warn('[loadQuizQuestions] No se encontró el objeto cuestionario para leer configuración.');
            }

            console.log('[loadQuizQuestions] Preguntas seleccionadas:', preguntasFinales.length);

            // 2. Cargar respuestas de texto libre y verdadero/falso
            let todasRespuestasTextoLibre = [];
            let todasRespuestasVF = [];
            try {
                const [resTextoLibre, resVF] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.cuestionarioRespuestaTextoLibreByCuestionario(cuestionarioId)),
                    axios.get(LMS_ENDPOINTS.cuestionarioRespuestaVerdaderoFalsoByCuestionario(cuestionarioId))
                ]);
                todasRespuestasTextoLibre = (resTextoLibre.data || []).map(Models.cuestionarioRespuestaTextoLibre);
                todasRespuestasVF = (resVF.data || []).map(Models.cuestionarioRespuestaVerdaderoFalso);
                console.log('[loadQuizQuestions] Respuestas texto libre:', todasRespuestasTextoLibre.length, '| VF:', todasRespuestasVF.length);
            } catch (err) {
                console.error('[loadQuizQuestions] Error cargando respuestas complementarias:', err);
            }

            // 3. Por cada pregunta, cargar sus respuestas/opciones
            const preguntasConOpciones = await Promise.all(
                preguntasFinales.map(async (pregunta) => {
                    const idPregunta = pregunta.idCuestionarioPreguntas || pregunta.IdCuestionarioPreguntas || pregunta.idPregunta || pregunta.IdPregunta;
                    const idTipoPreguntaRaw = pregunta.idTipoPregunta || pregunta.IdTipoPregunta || pregunta.idCatTipoPregunta || pregunta.IdCatTipoPregunta || 0;
                    const idTipoPregunta = parseInt(idTipoPreguntaRaw, 10);

                    console.log(`[loadQuizQuestions] Procesando pregunta ${idPregunta}, tipo: ${idTipoPregunta}`);

                    try {
                        // Si es texto libre (tipo 1)
                        if (idTipoPregunta === 1) {
                            const respTextoLibre = todasRespuestasTextoLibre.find(
                                (r) => r.idCuestionarioPreguntas === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                idTipoPregunta: idTipoPregunta,
                                tipo: "textoLibre",
                                respuestaTextoLibre: respTextoLibre,
                                opciones: [],
                            };
                        }

                        // Si es Verdadero/Falso (tipo 3)
                        if (idTipoPregunta === 3) {
                            const respVF = todasRespuestasVF.find(
                                (r) => (r.idCuestionarioPreguntas || r.IdCuestionarioPreguntas) === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                idTipoPregunta: idTipoPregunta,
                                tipo: "verdaderoFalso",
                                respuestaVerdaderoFalso: respVF,
                                opciones: [],
                            };
                        }

                        // Si es opción múltiple (tipo 2)
                        let respuesta = null;
                        try {
                            // 1. Obtener la respuesta configurada para esta pregunta (Optimizado)
                            const resRespuesta = await axios.get(LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultipleByPregunta(idPregunta));
                            // Algunos endpoints retornan objeto directo, otros array. Prevenimos.
                            const dataRaw = Array.isArray(resRespuesta.data) ? resRespuesta.data[0] : resRespuesta.data;
                            if (dataRaw) respuesta = dataRaw;
                        } catch (err) {
                            console.warn(`No se encontró respuesta configurada para pregunta ${idPregunta}`, err);
                        }

                        if (respuesta) {
                            const idRespuesta = respuesta.idRespuestaOpcionMultiple || respuesta.IdRespuestaOpcionMultiple;

                            // Cargar opciones asociadas a esta respuesta (Optimizado)
                            const resOpciones = await axios.get(LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultipleOpcionesByRespuesta(idRespuesta));
                            const todasOpciones = resOpciones.data || [];

                            const opcionesOrdenadas = todasOpciones
                                .filter((o) => (o.idRespuestaOpcionMultiple || o.IdRespuestaOpcionMultiple) === idRespuesta)
                                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idOpcion || o.IdOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                texto: o.texto || o.Texto || "",
                                esCorrecta: o.esCorrecta !== undefined ? o.esCorrecta : (o.EsCorrecta || false),
                                orden: o.orden || o.Orden || idx + 1,
                            }));

                            console.log(`[loadQuizQuestions] Pregunta ${idPregunta} - Opciones cargadas:`, opciones.length);

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "opcionMultiple",
                                opciones,
                                idRespuesta,
                                permiteMultiple: respuesta.permiteMultiple || respuesta.PermiteMultiple || false,
                            };
                        }

                        return { ...pregunta, idPregunta: idPregunta, tipo: "opcionMultiple", opciones: [] };
                    } catch (error) {
                        console.error(`[loadQuizQuestions] Error cargando opciones para pregunta ${idPregunta}:`, error);
                        return { ...pregunta, idPregunta: idPregunta, opciones: [] };
                    }
                })
            );

            console.log('[loadQuizQuestions] Preguntas procesadas:', preguntasConOpciones);
            setQuizPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error('[loadQuizQuestions] Error al cargar preguntas del cuestionario:', error);
            setQuizPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };

    // Función para cargar preguntas de un examen
    const loadExamQuestions = async (examenId) => {
        console.log('[loadExamQuestions] Iniciando carga para examen:', examenId);
        setLoadingQuestions(true);

        try {
            // 1. Obtener preguntas filtradas (Optimizado)
            const resPreguntas = await axios.get(LMS_ENDPOINTS.examenPreguntasByExamen(examenId));
            const preguntasDelExamen = resPreguntas.data || [];

            // 2. Obtener datos del examen (Optimizado por ID)
            let examen = null;
            try {
                const resExamen = await axios.get(LMS_ENDPOINTS.examenById(examenId));
                // Normalizar respuesta
                const dataRaw = Array.isArray(resExamen.data) ? resExamen.data[0] : resExamen.data;
                if (dataRaw) examen = dataRaw;
            } catch (e) {
                console.warn("No se pudo cargar datos del examen:", e);
            }

            // Lógica de aleatoriedad
            let preguntasFinales = [...preguntasDelExamen]; // Clonar

            console.log(`[loadExamQuestions] Buscando configuración para Examen ID: ${examenId}. Encontrado: ${!!examen}`);

            if (examen) {
                const cantidadAleatoria = examen.preguntasAleatorias || examen.PreguntasAleatorias || 0;
                console.log(`[loadExamQuestions] Configuración aleatoriedad: ${cantidadAleatoria}`);

                if (cantidadAleatoria > 0) {
                    console.log(`[loadExamQuestions] Aplicando mezcla y corte...`);

                    // Mezclar las preguntas (Fisher-Yates Shuffle)
                    for (let i = preguntasFinales.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [preguntasFinales[i], preguntasFinales[j]] = [preguntasFinales[j], preguntasFinales[i]];
                    }

                    // Tomar las primeras N (o todas si N >= length)
                    const cantidadATomar = Math.min(cantidadAleatoria, preguntasFinales.length);
                    preguntasFinales = preguntasFinales.slice(0, cantidadATomar);
                    console.log(`[loadExamQuestions] Resultado: ${preguntasFinales.length} preguntas.`);
                } else {
                    console.log('[loadExamQuestions] Sin aleatoriedad resultada (=0).');
                }
            } else {
                console.warn('[loadExamQuestions] No se encontró el objeto examen para leer configuración.');
            }

            console.log('[loadExamQuestions] Preguntas seleccionadas:', preguntasFinales.length);

            // 2. Cargar respuestas de texto libre y verdadero/falso
            let todasRespuestasTextoLibre = [];
            let todasRespuestasVF = [];
            try {
                const [resTextoLibre, resVF] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.examenPreguntaTextoLibreByExamen(examenId)),
                    axios.get(LMS_ENDPOINTS.examenPreguntaVerdaderoFalsoByExamen(examenId))
                ]);
                todasRespuestasTextoLibre = (resTextoLibre.data || []).map(Models.examenPreguntaTextoLibre);
                todasRespuestasVF = (resVF.data || []).map(Models.examenPreguntaVerdaderoFalso);
                console.log('[loadExamQuestions] Respuestas texto libre:', todasRespuestasTextoLibre.length, '| VF:', todasRespuestasVF.length);
            } catch (err) {
                console.error('[loadExamQuestions] Error cargando respuestas complementarias:', err);
            }

            // 3. Por cada pregunta, cargar sus respuestas/opciones
            const preguntasConOpciones = await Promise.all(
                preguntasFinales.map(async (pregunta) => {
                    const idPregunta = pregunta.idExamenPregunta || pregunta.IdExamenPregunta || pregunta.idExamenPreguntas || pregunta.IdExamenPreguntas || pregunta.idPregunta || pregunta.IdPregunta;
                    const idTipoPreguntaRaw = pregunta.idTipoPregunta || pregunta.IdTipoPregunta || pregunta.idCatTipoPregunta || pregunta.IdCatTipoPregunta || 0;
                    const idTipoPregunta = parseInt(idTipoPreguntaRaw, 10);

                    console.log(`[loadExamQuestions] Procesando pregunta ${idPregunta}, tipo: ${idTipoPregunta}`);

                    try {
                        // Si es texto libre (tipo 1)
                        if (idTipoPregunta === 1) {
                            const respTextoLibre = todasRespuestasTextoLibre.find(
                                (r) => r.idExamenPregunta === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                idTipoPregunta: idTipoPregunta,
                                tipo: "textoLibre",
                                respuestaTextoLibre: respTextoLibre,
                                opciones: [],
                            };
                        }

                        // Si es Verdadero/Falso (tipo 3)
                        if (idTipoPregunta === 3) {
                            const respVF = todasRespuestasVF.find(
                                (r) => (r.idExamenPregunta || r.IdExamenPregunta || r.idExamenPreguntas || r.IdExamenPreguntas) === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                idTipoPregunta: idTipoPregunta,
                                tipo: "verdaderoFalso",
                                respuestaVerdaderoFalso: respVF,
                                opciones: [],
                            };
                        }

                        // Si es opción múltiple (tipo 2)
                        let respuesta = null;
                        try {
                            // 1. Obtener la respuesta configurada para esta pregunta (Optimizado)
                            const resResp = await axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleByPregunta(idPregunta));
                            // Algunos endpoints retornan lista, otros objeto
                            const dataRaw = Array.isArray(resResp.data) ? resResp.data[0] : resResp.data;
                            if (dataRaw) respuesta = dataRaw;
                        } catch (err) {
                            console.warn(`No se encontró respuesta configurada para pregunta de examen ${idPregunta}`, err);
                        }

                        if (respuesta) {
                            // El ID correcto es idExamenRespuestaOpcionMultiple
                            const idRespuesta = respuesta.idExamenRespuestaOpcionMultiple || respuesta.IdExamenRespuestaOpcionMultiple;

                            console.log(`[loadExamQuestions] Pregunta ${idPregunta} - ID Respuesta OM: ${idRespuesta}`);

                            // Cargar opciones asociadas a esta respuesta (Optimizado)
                            const resOpciones = await axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpcionesByRespuesta(idRespuesta));
                            const todasOpciones = resOpciones.data || [];

                            const opcionesOrdenadas = todasOpciones
                                .filter((o) => (o.idExamenRespuestaOpcionMultiple || o.IdExamenRespuestaOpcionMultiple) === idRespuesta)
                                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idExamenOpcion || o.IdExamenOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                texto: o.texto || o.Texto || "",
                                esCorrecta: o.esCorrecta !== undefined ? o.esCorrecta : (o.EsCorrecta || false),
                                orden: o.orden || o.Orden || idx + 1,
                            }));

                            console.log(`[loadExamQuestions] Pregunta ${idPregunta} - Opciones cargadas:`, opciones.length);

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "opcionMultiple",
                                opciones,
                                idRespuesta,
                                permiteMultiple: respuesta.permiteMultiple || respuesta.PermiteMultiple || false,
                            };
                        }

                        return { ...pregunta, idPregunta: idPregunta, tipo: "opcionMultiple", opciones: [] };
                    } catch (error) {
                        console.error(`[loadExamQuestions] Error cargando opciones para pregunta ${idPregunta}:`, error);
                        return { ...pregunta, idPregunta: idPregunta, opciones: [] };
                    }
                })
            );

            console.log('[loadExamQuestions] Preguntas procesadas:', preguntasConOpciones);
            setExamPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error('[loadExamQuestions] Error al cargar preguntas del examen:', error);
            setExamPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };


    // Todos los elementos (lecciones, cuestionarios, exámenes) en orden
    const flatAllContent = useMemo(
        () =>
            sidebarModules.flatMap((m) =>
                (m.items || []).map((item) => ({
                    ...item,
                    moduleId: m.moduleId,
                    moduleTitle: m.moduleTitle ||
                        (course ? course.nombre || course.Nombre || course.titulo : ""),
                }))
            ),
        [sidebarModules, course]
    );

    const flatLessons = useMemo(
        () =>
            sidebarModules.flatMap((m) =>
                (m.items || [])
                    .filter((item) => item.tipo === "LECCION")
                    .map((item) => ({
                        ...item,
                        moduleTitle:
                            m.moduleTitle ||
                            (course ? course.nombre || course.Nombre || course.titulo : ""),
                    }))
            ),
        [sidebarModules, course]
    );

    const totalElements = flatLessons.length;

    const completedCount = useMemo(
        () => flatLessons.filter((x) => x.completado).length,
        [flatLessons]
    );

    const topProgress = useMemo(
        () =>
            totalElements === 0
                ? 0
                : Math.round((completedCount / totalElements) * 100),
        [completedCount, totalElements]
    );

    // Índice actual en flatAllContent (incluye todos los tipos)
    const currentIndexInAll = useMemo(() => {
        if (currentContentType === "LECCION" && currentLessonId) {
            return flatAllContent.findIndex(
                (x) => x.tipo === "LECCION" && String(x.lessonId) === String(currentLessonId)
            );
        } else if (currentContentType === "CUESTIONARIO" && currentContentId) {
            return flatAllContent.findIndex(
                (x) => x.tipo === "CUESTIONARIO" && String(x.id) === String(currentContentId)
            );
        } else if (currentContentType === "EXAMEN" && currentContentId) {
            return flatAllContent.findIndex(
                (x) => x.tipo === "EXAMEN" && String(x.id) === String(currentContentId)
            );
        }
        return 0;
    }, [flatAllContent, currentContentType, currentLessonId, currentContentId]);

    const currentIndex = useMemo(() => {
        const idx = flatLessons.findIndex(
            (x) => String(x.lessonId) === String(currentLessonId)
        );
        return idx === -1 ? 0 : idx;
    }, [flatLessons, currentLessonId]);

    const currentItem =
        flatLessons.length > 0 ? flatLessons[currentIndex] || null : null;

    const modulesWithLessons = useMemo(
        () => sidebarModules,
        [sidebarModules]
    );

    const handleClickContent = (item) => {
        const { tipo, id, lessonId: lid, quizId, examId, lessonTitle } = item;

        setCurrentContentType(tipo);
        setCurrentContentId(id);
        setCurrentContentTitle(lessonTitle || "");
        setRespuestasUsuario({});
        setQuizSubmitted(false);
        setQuizResult(null);
        setExamSubmitted(false);
        setExamResult(null);

        if (tipo === "LECCION") {
            setCurrentLessonId(lid);
            navigate(`/menu/lms/curso-preview/${slug}/player/${lid}`);
        } else if (tipo === "CUESTIONARIO") {
            // Ahora navegamos con query parameter para cuestionarios
            setCurrentLessonId(null);
            setLessonSections([]);
            navigate(`/menu/lms/curso-preview/${slug}/player/${quizId}?type=quiz`);
        } else if (tipo === "EXAMEN") {
            // Ahora navegamos con query parameter para exámenes
            setCurrentLessonId(null);
            setLessonSections([]);
            navigate(`/menu/lms/curso-preview/${slug}/player/${examId}?type=exam`);
        }
    };

    const handleClickLesson = (id) => {
        const item = flatLessons.find((l) => l.lessonId === id);
        if (item) {
            handleClickContent(item);
        }
    };

    const handleToggleComplete = () => {
        if (!currentItem) return;

        const targetLessonId = currentItem.lessonId;

        setSidebarModules((prev) => {
            const updated = prev.map((mod) => ({
                ...mod,
                items: (mod.items || []).map((item) =>
                    item.tipo === "LECCION" && String(item.lessonId) === String(targetLessonId)
                        ? { ...item, completado: !item.completado }
                        : item
                ),
            }));

            const flat = updated.flatMap((m) => m.items || []);
            const refreshed = flat.find(
                (x) => x.tipo === "LECCION" && String(x.lessonId) === String(targetLessonId)
            );

            if (refreshed?.lessonTitle) {
                setJustCompletedTitle(
                    refreshed.completado ? refreshed.lessonTitle : ""
                );
                if (refreshed.completado) {
                    setTimeout(() => setJustCompletedTitle(""), 4000);
                }
            }

            return updated;
        });
    };

    const goPrev = () => {
        if (currentIndexInAll <= 0) return;
        const prev = flatAllContent[currentIndexInAll - 1];
        if (prev) handleClickContent(prev);
    };

    const goNext = () => {
        // Usar flatAllContent para navegación correcta
        if (currentIndexInAll >= flatAllContent.length - 1) {
            // Si es la última actividad, redirigir al currículum
            navigate(`/menu/lms/curso/${slug}`);
            return;
        }
        const next = flatAllContent[currentIndexInAll + 1];
        if (next) {
            handleClickContent(next);
        }
    };

    useEffect(() => {
        const targetLessonId = lessonId || currentLessonId;
        const loadLessonContent = async () => {
            if (!targetLessonId) {
                setLessonSections([]);
                return;
            }

            setLoadingLessonContent(true);
            try {
                const response = await axios.get(LMS_ENDPOINTS.leccionesContenido);
                const allContent = response.data || [];
                const lessonContent = allContent.filter(
                    (c) =>
                        String(c.idLeccion || c.IdLeccion) === String(targetLessonId) &&
                        (c.activo === true ||
                            c.Activo === true ||
                            (c.activo === undefined && c.Activo === undefined))
                );

                const sections = lessonContent
                    .map((item) => {
                        try {
                            const parsed = JSON.parse(item.valor || item.Valor);
                            return {
                                ...parsed,
                                id: item.idContenido || item.IdContenido,
                            };
                        } catch (e) {
                            console.error("[CoursePlayerPreview] Error parsing lesson content valor:", e);
                            return null;
                        }
                    })
                    .filter(Boolean)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));

                setLessonSections(sections);
            } catch (error) {
                console.error("[CoursePlayerPreview] Error loading lesson content:", error);
                setLessonSections([]);
            } finally {
                setLoadingLessonContent(false);
            }
        };

        loadLessonContent();
    }, [lessonId, currentLessonId]);

    /* FUNCIÓN VIEJA - REEMPLAZADA POR LA NUEVA QUE CARGA RESPUESTAS DE TEXTO LIBRE
    const loadQuizQuestions = async (quizId) => {
        setLoadingQuestions(true);
        try {
            const resPreguntas = await axios.get(LMS_ENDPOINTS.cuestionariosPreguntas);
            const todasPreguntas = resPreguntas.data || [];
    
            const preguntasDelCuestionario = todasPreguntas.filter(
                (p) => (p.idCuestionario || p.IdCuestionario) === parseInt(quizId, 10)
            );
    
            const preguntasConOpciones = await Promise.all(
                preguntasDelCuestionario.map(async (pregunta) => {
                    const idPregunta =
                        pregunta.idCuestionarioPreguntas ||
                        pregunta.IdCuestionarioPreguntas ||
                        pregunta.idCuestionarioPregunta ||
                        pregunta.IdCuestionarioPregunta;
    
                    try {
                        const resRespuestas = await axios.get(
                            LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultiple
                        );
                        const todasRespuestas = resRespuestas.data || [];
    
                        const respuesta = todasRespuestas.find(
                            (r) =>
                                (r.idCuestionarioPreguntas ||
                                    r.IdCuestionarioPreguntas ||
                                    r.idCuestionarioPregunta ||
                                    r.IdCuestionarioPregunta) === idPregunta
                        );
    
                        if (respuesta) {
                            const idRespuesta =
                                respuesta.idRespuestaOpcionMultiple ||
                                respuesta.IdRespuestaOpcionMultiple ||
                                respuesta.idRespuesta ||
                                respuesta.IdRespuesta;
    
                            const resOpciones = await axios.get(
                                LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultipleOpciones
                            );
                            const todasOpciones = resOpciones.data || [];
    
                            const opcionesOrdenadas = todasOpciones
                                .filter(
                                    (o) =>
                                        (o.idRespuestaOpcionMultiple ||
                                            o.IdRespuestaOpcionMultiple) === idRespuesta
                                )
                                .sort(
                                    (a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0)
                                );
    
                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idOpcion || o.IdOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                esCorrecta:
                                    o.esCorrecta !== undefined
                                        ? o.esCorrecta
                                        : o.EsCorrecta !== undefined
                                            ? o.EsCorrecta
                                            : false,
                                orden: o.orden || o.Orden || idx + 1,
                            }));
    
                            return {
                                ...pregunta,
                                idPregunta,
                                descripcion:
                                    pregunta.descripcionPregunta ||
                                    pregunta.DescripcionPregunta ||
                                    "",
                                opciones,
                                idRespuesta,
                            };
                        }
    
                        return { ...pregunta, idPregunta, opciones: [] };
                    } catch (error) {
                        console.error(
                            `[CoursePlayerPreview] Error cargando opciones para pregunta ${idPregunta}:`,
                            error
                        );
                        return { ...pregunta, idPregunta, opciones: [] };
                    }
                })
            );
    
            setQuizPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error("[CoursePlayerPreview] Error al cargar preguntas del cuestionario:", error);
            setQuizPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };
    
    const loadExamQuestions = async (examId) => {
        setLoadingQuestions(true);
        try {
            const [resPreguntas, resCabecerasOM, resOpcionesOM] = await Promise.all([
                axios.get(LMS_ENDPOINTS.examenPreguntas),
                axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultiple),
                axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones),
            ]);
    
            const todasPreguntas = (resPreguntas.data || []).map(Models.examenPreguntas);
            const todasCabecerasOM = (resCabecerasOM.data || []).map(
                Models.examenPreguntaOpcionMultiple
            );
            const todasOpcionesOM = (resOpcionesOM.data || []).map(
                Models.examenPreguntaOpcionMultipleOpcion
            );
    
            const preguntasDelExamen = todasPreguntas.filter(
                (p) => p.idExamen === parseInt(examId, 10)
            );
    
            const preguntasConOpciones = preguntasDelExamen.map((pregunta) => {
                const idPregunta = pregunta.idExamenPregunta;
    
                const cabecera = todasCabecerasOM.find(
                    (r) => r.idExamenPregunta === idPregunta
                );
    
                if (!cabecera) {
                    return { ...pregunta, opciones: [], idRespuesta: null };
                }
    
                const idRespuestaOM = cabecera.idExamenRespuestaOpcionMultiple;
    
                const opcionesOrdenadas = todasOpcionesOM
                    .filter((o) => o.idExamenRespuestaOpcionMultiple === idRespuestaOM)
                    .sort((a, b) => (a.orden || 0) - (b.orden || 0));
    
                const opciones = opcionesOrdenadas.map((o, idx) => ({
                    ...o,
                    idOpcion:
                        o.idOpcion ||
                        o.IdOpcion ||
                        o.idExamenPreguntaOpcionMultiple ||
                        o.IdExamenPreguntaOpcionMultiple ||
                        `${idPregunta}-${idx}`,
                    llave: String.fromCharCode(65 + idx),
                    descripcion: o.texto || "",
                    esCorrecta:
                        o.esCorrecta !== undefined
                            ? o.esCorrecta
                            : o.EsCorrecta !== undefined
                                ? o.EsCorrecta
                                : false,
                    orden: o.orden || idx + 1,
                }));
    
                return {
                    ...pregunta,
                    idPregunta,
                    descripcion:
                        pregunta.descripcionPregunta || pregunta.DescripcionPregunta || "",
                    opciones,
                    idRespuesta: idRespuestaOM,
                };
            });
    
            setExamPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error("[CoursePlayerPreview] Error al cargar preguntas del examen:", error);
            setExamPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };
    
    */ // FIN DE FUNCIONES VIEJAS - USAR LAS NUEVAS IMPLEMENTADAS ARRIBA
    const renderLessonSections = () => {
        if (loadingLessonContent) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando contenido de la lección...
                </div>
            );
        }

        if (!lessonSections || lessonSections.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Esta lección aún no tiene contenido configurado.
                </div>
            );
        }

        return lessonSections.map((section, index) => (
            <div
                key={section.id}
                style={{
                    marginBottom: 0,
                    backgroundColor: section.style?.backgroundColor || "#ffffff",
                    padding: section.style?.padding ?? 20,
                    borderRadius:
                        index === 0
                            ? index === lessonSections.length - 1
                                ? "8px"
                                : "8px 8px 0 0"
                            : index === lessonSections.length - 1
                                ? "0 0 8px 8px"
                                : "0",
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            section.columns === 2
                                ? `${section.splitRatio || 50}fr ${100 - (section.splitRatio || 50)}fr`
                                : "1fr",
                        gap: section.style?.gap ?? 20,
                    }}
                >
                    {(section.children || []).map((column, colIdx) => (
                        <div key={colIdx} style={{
                            minHeight: 40,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            {(column.children || []).length === 0 && (
                                <div
                                    style={{
                                        textAlign: "center",
                                        color: "#cbd5f5",
                                        fontSize: "0.85rem",
                                        padding: 16,
                                    }}
                                ></div>
                            )}
                            {(column.children || []).map((element) => (
                                <div
                                    key={element.id}
                                    style={{
                                        ...element.style,
                                        // Force auto height for text elements so they grow when wrapping
                                        height: (element.type === 'text' || element.type === 'h1' || element.type === 'h2') ? 'auto' : element.style?.height,
                                        border: "none",
                                        boxSizing: "border-box",
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}
                                >
                                    {element.type === "text" ||
                                        element.type === "h1" ||
                                        element.type === "h2" ? (
                                        <div
                                            style={{
                                                userSelect: 'none',
                                                overflowWrap: 'break-word',
                                                wordWrap: 'break-word',
                                                maxWidth: '100%',
                                                whiteSpace: 'normal'
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: (element.content || "").replace(
                                                    /\n/g,
                                                    "<br />"
                                                ),
                                            }}
                                        />
                                    ) : element.type === "image" ? (
                                        <img
                                            src={element.src}
                                            alt="Contenido de la lección"
                                            style={{
                                                maxWidth: "100%",
                                                height: element.style?.height || "auto",
                                                objectFit: "cover",
                                                borderRadius: 8,
                                            }}
                                        />
                                    ) : element.type === "video" ? (
                                        element.src ? (
                                            <video
                                                src={
                                                    element.src.startsWith("http")
                                                        ? element.src
                                                        : `${SERVER_URL}${element.src}`
                                                }
                                                controls
                                                controlsList="nodownload"
                                                onContextMenu={(e) => e.preventDefault()}
                                                style={{
                                                    width: element.style?.width || "100%",
                                                    height: element.style?.height || 300,
                                                    borderRadius: 8,
                                                    backgroundColor: "#000",
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    backgroundColor: "#000",
                                                    height: element.style?.height || 300,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#fff",
                                                    borderRadius: 8,
                                                }}
                                            >
                                                <span style={{ fontSize: "1.2rem" }}>
                                                    Video no configurado
                                                </span>
                                            </div>
                                        )
                                    ) : element.type === "iframe" ? (
                                        element.src ? (
                                            <iframe
                                                src={formatUrl(element.src)}
                                                title={element.content || "Contenido externo"}
                                                style={{
                                                    width: element.style?.width || "100%",
                                                    height: element.style?.height || "400px",
                                                    border: "none",
                                                    borderRadius: 8,
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    backgroundColor: "#f3f4f6",
                                                    height: element.style?.height || 400,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#6b7280",
                                                    borderRadius: 8,
                                                }}
                                            >
                                                <span style={{ fontSize: "1.2rem" }}>
                                                    Sitio web no configurado
                                                </span>
                                            </div>
                                        )
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    const handleSelectOption = (idPregunta, idOpcion, permiteMultiple = false) => {
        setRespuestasUsuario((prev) => {
            const current = prev[idPregunta];

            if (permiteMultiple) {
                const currentArray = Array.isArray(current) ? current : (current ? [current] : []);
                const idString = String(idOpcion);

                // Verificar si ya está seleccionado
                const exists = currentArray.some(id => String(id) === idString);

                if (exists) {
                    return {
                        ...prev,
                        [idPregunta]: currentArray.filter(id => String(id) !== idString)
                    };
                } else {
                    return {
                        ...prev,
                        [idPregunta]: [...currentArray, idOpcion]
                    };
                }
            } else {
                return {
                    ...prev,
                    [idPregunta]: idOpcion,
                };
            }
        });
    };

    const handleQuizButtonClick = () => {
        if (!quizSubmitted) {
            handleSubmitQuiz();
        } else {
            goNext();
        }
    };

    const handleExamButtonClick = () => {
        if (!examSubmitted) {
            handleSubmitExam();
        } else {
            goNext();
        }
    };

    const handleSubmitQuiz = () => {
        if (!quizPreguntas || quizPreguntas.length === 0) return;

        let correctas = 0;
        let contestadas = 0;

        quizPreguntas.forEach((pregunta) => {
            const idPregunta = pregunta.idPregunta;

            // Verificar si es pregunta con espacios en blanco
            const descripcion = pregunta.descripcionPregunta || pregunta.DescripcionPregunta || pregunta.descripcion || '';
            const tieneEspaciosBlanco = descripcion.includes('{}');

            if (tieneEspaciosBlanco) {
                // Contar respuestas de espacios en blanco
                let tieneRespuesta = false;
                const respuestasBlanco = [];

                const blanksCount = descripcion.split('{}').length - 1;
                for (let i = 0; i < blanksCount; i++) {

                    const respuesta = respuestasUsuario[`${idPregunta}_blank_${i}`] || "";
                    if (respuesta && respuesta.trim()) {
                        tieneRespuesta = true;
                    }
                    respuestasBlanco.push(respuesta);
                }

                if (tieneRespuesta) {
                    contestadas += 1;

                    // Verificar si es correcta
                    let esCorrecta = false;

                    // Obtener respuestas correctas
                    let respuestasCorrectas = [];

                    if (pregunta.respuestaTextoLibre) {
                        const palabrasClave = pregunta.respuestaTextoLibre.palabrasClave ||
                            pregunta.respuestaTextoLibre.PalabrasClave || '';
                        if (palabrasClave) {
                            respuestasCorrectas = [palabrasClave];
                        }
                    }

                    // Si no hay respuestas de texto libre, buscar en opciones correctas
                    if (respuestasCorrectas.length === 0 && pregunta.opciones) {
                        respuestasCorrectas = pregunta.opciones
                            .filter(o => o.esCorrecta || o.EsCorrecta || o.correcta)
                            .map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || '');
                    }

                    // Comparar respuestas (case-insensitive y trimmed)
                    if (respuestasCorrectas.length > 0) {
                        esCorrecta = true;
                        for (let i = 0; i < respuestasBlanco.length; i++) {
                            const respuestaUsuario = (respuestasBlanco[i] || '').trim().toLowerCase();
                            const respuestaCorrecta = (respuestasCorrectas[i] || respuestasCorrectas[0] || '').trim().toLowerCase();

                            if (respuestaUsuario !== respuestaCorrecta) {
                                esCorrecta = false;
                                break;
                            }
                        }
                    } else {
                        // Si no hay respuestas correctas definidas, marcar como correcta
                        esCorrecta = true;
                    }

                    if (esCorrecta) {
                        correctas += 1;
                    }
                }
            } else if (pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && parseInt(pregunta.idTipoPregunta, 10) === 3)) {
                const val = respuestasUsuario[idPregunta];
                if (val !== undefined && val !== null) {
                    contestadas += 1;
                    const objVF = pregunta.respuestaVerdaderoFalso;
                    const correcta = objVF?.respuestaCorrecta !== undefined ? objVF.respuestaCorrecta : objVF?.RespuestaCorrecta;

                    if (String(val) === String(correcta)) {
                        correctas += 1;
                    }
                }
            } else {
                // Pregunta de opción múltiple
                const selectedVal = respuestasUsuario[idPregunta];
                if (!selectedVal || (Array.isArray(selectedVal) && selectedVal.length === 0)) return;
                contestadas += 1;

                const opciones = pregunta.opciones || [];
                let todasCorrectas = false;

                if (pregunta.permiteMultiple && Array.isArray(selectedVal)) {
                    // Lógica para múltiples respuestas
                    const correctIds = opciones
                        .filter(o => o.esCorrecta || o.EsCorrecta)
                        .map(o => String(o.idOpcion || o.IdOpcion));

                    const selectedIds = selectedVal.map(String);

                    // Verificar coincidencia exacta
                    if (selectedIds.length === correctIds.length &&
                        selectedIds.every(id => correctIds.includes(id))) {
                        todasCorrectas = true;
                    }
                } else {
                    const seleccionada = opciones.find(
                        (o) => String(o.idOpcion || o.IdOpcion) === String(selectedVal)
                    );

                    if (seleccionada) {
                        todasCorrectas = seleccionada.esCorrecta !== undefined
                            ? seleccionada.esCorrecta
                            : (seleccionada.EsCorrecta || false);
                    }
                }

                if (todasCorrectas) {
                    correctas += 1;
                }
            }
        });

        const incorrectas = contestadas - correctas;
        setQuizSubmitted(true);
        setQuizResult({
            total: quizPreguntas.length,
            contestadas,
            correctas,
            incorrectas,
        });

        const calificacion = contestadas > 0 ? Math.round((correctas / quizPreguntas.length) * 100) : 0;
        const aprobado = calificacion >= 60;

        setResultsData({
            correctas,
            incorrectas,
            calificacion,
            aprobado
        });
        setShowResultsModal(true);
    };

    const handleSubmitExam = () => {
        if (!examPreguntas || examPreguntas.length === 0) return;

        let correctas = 0;
        let contestadas = 0;

        examPreguntas.forEach((pregunta) => {
            const idPregunta = pregunta.idPregunta;

            // Verificar si es pregunta con espacios en blanco
            const descripcion = pregunta.descripcionPregunta || pregunta.DescripcionPregunta || pregunta.descripcion || '';
            const tieneEspaciosBlanco = descripcion.includes('{}');

            if (tieneEspaciosBlanco) {
                // Contar respuestas de espacios en blanco
                let tieneRespuesta = false;
                const respuestasBlanco = [];

                const blanksCount = descripcion.split('{}').length - 1;
                for (let i = 0; i < blanksCount; i++) {

                    const respuesta = respuestasUsuario[`${idPregunta}_blank_${i}`] || "";
                    if (respuesta && respuesta.trim()) {
                        tieneRespuesta = true;
                    }
                    respuestasBlanco.push(respuesta);
                }

                if (tieneRespuesta) {
                    contestadas += 1;

                    // Verificar si es correcta
                    let esCorrecta = false;

                    // Obtener respuestas correctas
                    let respuestasCorrectas = [];

                    if (pregunta.respuestaTextoLibre) {
                        const palabrasClave = pregunta.respuestaTextoLibre.palabrasClave ||
                            pregunta.respuestaTextoLibre.PalabrasClave || '';
                        if (palabrasClave) {
                            respuestasCorrectas = [palabrasClave];
                        }
                    }

                    // Si no hay respuestas de texto libre, buscar en opciones correctas
                    if (respuestasCorrectas.length === 0 && pregunta.opciones) {
                        respuestasCorrectas = pregunta.opciones
                            .filter(o => o.esCorrecta || o.EsCorrecta || o.correcta)
                            .map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || '');
                    }

                    // Comparar respuestas (case-insensitive y trimmed)
                    if (respuestasCorrectas.length > 0) {
                        esCorrecta = true;
                        for (let i = 0; i < respuestasBlanco.length; i++) {
                            const respuestaUsuario = (respuestasBlanco[i] || '').trim().toLowerCase();
                            const respuestaCorrecta = (respuestasCorrectas[i] || respuestasCorrectas[0] || '').trim().toLowerCase();

                            if (respuestaUsuario !== respuestaCorrecta) {
                                esCorrecta = false;
                                break;
                            }
                        }
                    } else {
                        // Si no hay respuestas correctas definidas, marcar como correcta
                        esCorrecta = true;
                    }

                    if (esCorrecta) {
                        correctas += 1;
                    }
                }
            } else if (pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && parseInt(pregunta.idTipoPregunta, 10) === 3)) {
                const val = respuestasUsuario[idPregunta];
                if (val !== undefined && val !== null) {
                    contestadas += 1;
                    const objVF = pregunta.respuestaVerdaderoFalso;
                    const correcta = objVF?.respuestaCorrecta !== undefined ? objVF.respuestaCorrecta : objVF?.RespuestaCorrecta;

                    if (String(val) === String(correcta)) {
                        correctas += 1;
                    }
                }
            } else {
                // Pregunta de opción múltiple
                const selectedVal = respuestasUsuario[idPregunta];
                if (!selectedVal || (Array.isArray(selectedVal) && selectedVal.length === 0)) return;
                contestadas += 1;

                const opciones = pregunta.opciones || [];
                let todasCorrectas = false;

                if (pregunta.permiteMultiple && Array.isArray(selectedVal)) {
                    // Lógica para múltiples respuestas
                    const correctIds = opciones
                        .filter(o => o.esCorrecta || o.EsCorrecta)
                        .map(o => String(o.idOpcion || o.IdOpcion));

                    const selectedIds = selectedVal.map(String);

                    // Verificar coincidencia exacta
                    if (selectedIds.length === correctIds.length &&
                        selectedIds.every(id => correctIds.includes(id))) {
                        todasCorrectas = true;
                    }
                } else {
                    const seleccionada = opciones.find(
                        (o) => String(o.idOpcion || o.IdOpcion) === String(selectedVal)
                    );

                    if (seleccionada) {
                        todasCorrectas = seleccionada.esCorrecta !== undefined
                            ? seleccionada.esCorrecta
                            : (seleccionada.EsCorrecta || false);
                    }
                }

                if (todasCorrectas) {
                    correctas += 1;
                }
            }
        });

        const incorrectas = contestadas - correctas;
        setExamSubmitted(true);
        setExamResult({
            total: examPreguntas.length,
            contestadas,
            correctas,
            incorrectas,
        });

        const calificacion = contestadas > 0 ? Math.round((correctas / examPreguntas.length) * 100) : 0;
        const aprobado = calificacion >= 60;

        setResultsData({
            correctas,
            incorrectas,
            calificacion,
            aprobado
        });
        setShowResultsModal(true);
    };

    // Función helper para renderizar preguntas con espacios en blanco
    const renderQuestionWithBlanks = (descripcion, idPregunta, preguntaCompleta) => {
        console.log('=== DEBUG renderQuestionWithBlanks ===');
        console.log('Descripción:', descripcion);
        console.log('ID Pregunta:', idPregunta);
        console.log('Pregunta completa:', preguntaCompleta);

        // Si no hay {}, renderizar normalmente
        if (!descripcion.includes('{}')) {
            return <p style={{ margin: "0 0 0.75rem 0", color: "#0f172a" }}>{descripcion}</p>;
        }

        // Dividir la descripción por {}
        const parts = descripcion.split('{}');
        const blanksCount = parts.length - 1;

        console.log('Número de espacios en blanco:', blanksCount);

        // Obtener las respuestas correctas
        let correctAnswers = [];

        // Primero intentar obtener de respuestaTextoLibre (preguntas de texto libre)
        if (preguntaCompleta && preguntaCompleta.respuestaTextoLibre) {
            const palabrasClave = preguntaCompleta.respuestaTextoLibre.palabrasClave ||
                preguntaCompleta.respuestaTextoLibre.PalabrasClave ||
                "";
            console.log('Palabras clave encontradas:', palabrasClave);

            if (palabrasClave) {
                // Si hay múltiples espacios en blanco, dividir las palabras clave
                // Asumimos que están separadas por algún delimitador o es una sola respuesta
                correctAnswers = [palabrasClave];
            }
        }

        // Si no hay respuestas de texto libre, buscar en opciones (opción múltiple)
        if (correctAnswers.length === 0 && preguntaCompleta && preguntaCompleta.opciones) {
            console.log('Buscando en opciones:', preguntaCompleta.opciones);
            correctAnswers = preguntaCompleta.opciones
                .filter(o => o.esCorrecta || o.EsCorrecta || o.correcta)
                .map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || "");
        }

        console.log('Respuestas correctas finales:', correctAnswers);

        // Console log para debugging - mostrar pregunta con respuestas
        let preguntaConRespuestas = descripcion;
        correctAnswers.forEach((respuesta, idx) => {
            preguntaConRespuestas = preguntaConRespuestas.replace('{}', `[${respuesta}]`);
        });
        console.log(`[Pregunta ID: ${idPregunta}] ${preguntaConRespuestas}`);

        return (
            <div style={{ margin: "0 0 0.75rem 0", color: "#0f172a", lineHeight: "2" }}>
                {parts.map((part, idx) => {
                    // Determinar qué valor mostrar en el input
                    const userAnswer = respuestasUsuario[`${idPregunta}_blank_${idx}`] || "";
                    
                    // Obtener la respuesta correcta para este blank con fallback al primero si solo hay uno definido
                    const correctAnswer = (correctAnswers[idx] || correctAnswers[0] || "").trim().toLowerCase();
                    const maxLength = correctAnswer.length > 0 ? correctAnswer.length + 10 : 50;

                    // Determinar si la respuesta es correcta (comparación case-insensitive y trimmed)
                    // Si no hay respuestas correctas definidas (correctAnswers.length === 0), se considera correcta por defecto para coincidir con el backend
                    const isCorrect = correctAnswers.length === 0 || userAnswer.trim().toLowerCase() === correctAnswer;
                    const isSubmitted = quizSubmitted || examSubmitted;

                    // Determinar el color del borde según si es correcto o no
                    let borderColor = "#cbd5e1"; // Default
                    let backgroundColor = "#ffffff";

                    if (isSubmitted && userAnswer.trim()) {
                        borderColor = isCorrect ? "#10b981" : "#ef4444"; // Verde si correcto, rojo si incorrecto
                        backgroundColor = isCorrect ? "#f0fdf4" : "#fef2f2"; // Fondo verde claro o rojo claro
                    } else if (isSubmitted) {
                        // Si no contestó, pero se considera correcta por falta de definición
                        if (isCorrect && correctAnswers.length === 0) {
                            borderColor = "#10b981";
                            backgroundColor = "#f0fdf4";
                        } else {
                            backgroundColor = "#f1f5f9";
                        }
                    }

                    // Determinar qué valor mostrar en el input
                    let displayValue = userAnswer;
                    if (isSubmitted && !isCorrect && correctAnswer) {
                        // Si está enviado, es incorrecto, NO revelar correcta
                        displayValue = userAnswer;
                    }

                    return (
                        <React.Fragment key={idx}>
                            <span>{part}</span>
                            {idx < blanksCount && (
                                <input
                                    type="text"
                                    style={{
                                        display: "inline-block",
                                        minWidth: "120px",
                                        padding: "4px 8px",
                                        margin: "0 4px",
                                        border: `2px solid ${borderColor}`,
                                        borderRadius: "4px",
                                        fontSize: "0.9rem",
                                        backgroundColor: backgroundColor,
                                        transition: "all 0.3s ease",
                                    }}
                                    placeholder="Escribe aquí..."
                                    maxLength={isSubmitted ? 200 : maxLength}
                                    value={displayValue}
                                    onChange={(e) => {
                                        if (!quizSubmitted && !examSubmitted) {
                                            setRespuestasUsuario(prev => ({
                                                ...prev,
                                                [`${idPregunta}_blank_${idx}`]: e.target.value
                                            }));
                                        }
                                    }}
                                    disabled={quizSubmitted || examSubmitted}
                                    title={""}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    const renderQuizView = () => {

        if (loadingQuestions) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando preguntas del cuestionario...
                </div>
            );
        }

        if (!quizPreguntas || quizPreguntas.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Este cuestionario aún no tiene preguntas configuradas.
                </div>
            );
        }

        return (
            <div style={{ marginTop: "1.5rem" }}>
                {quizPreguntas.map((pregunta, index) => {
                    const descripcion =
                        pregunta.descripcionPregunta ||
                        pregunta.DescripcionPregunta ||
                        pregunta.descripcion ||
                        "";

                    return (
                        <div
                            key={pregunta.idPregunta || index}
                            style={{
                                border: "1px solid #e2e8f0",
                                borderRadius: 8,
                                padding: "1rem 1.25rem",
                                marginBottom: "1rem",
                                backgroundColor: "#ffffff",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "0.5rem",
                                }}
                            >
                                <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: 0 }}>
                                    Pregunta {index + 1}
                                    {pregunta.permiteMultiple && (
                                        <span style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: "normal", marginLeft: "8px" }}>
                                            (más de una respuesta)
                                        </span>
                                    )}
                                </h3>
                            </div>

                            {renderQuestionWithBlanks(descripcion, pregunta.idPregunta, pregunta)}

                            {/* Verdadero / Falso */}
                            {(pregunta.tipo === "verdaderoFalso" || parseInt(pregunta.idTipoPregunta || pregunta.IdTipoPregunta, 10) === 3) && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', marginBottom: '1rem' }}>
                                    {[true, false].map((val) => {
                                        const isTruth = val === true;
                                        const labelText = isTruth ? "Verdadero" : "Falso";
                                        const letter = isTruth ? "A" : "B";

                                        const userAns = respuestasUsuario[pregunta.idPregunta];
                                        const isSelected = String(userAns) === String(val);

                                        const correctAnswer = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta !== undefined
                                            ? pregunta.respuestaVerdaderoFalso?.respuestaCorrecta
                                            : pregunta.respuestaVerdaderoFalso?.RespuestaCorrecta;

                                        const isCorrectAnswer = String(correctAnswer) === String(val);

                                        let backgroundColor = "transparent";
                                        let border = "1px solid #e2e8f0";
                                        let letterStyle = {};

                                        // Feedback V/F
                                        if (quizSubmitted) {
                                            if (isCorrectAnswer) {
                                                backgroundColor = "#ecfdf3";
                                                border = "1px solid #22c55e";
                                                letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                            } else if (isSelected) {
                                                backgroundColor = "#fef2f2";
                                                border = "1px solid #ef4444";
                                                letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                            }
                                        } else if (isSelected) {
                                            // Verde al seleccionar durante el intento
                                            backgroundColor = "#ecfdf3";
                                            border = "1px solid #22c55e";
                                            letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                        }

                                        return (
                                            <div
                                                key={String(val)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "0.4rem 0.6rem",
                                                    borderRadius: 6,
                                                    backgroundColor,
                                                    border,
                                                    flex: 1,
                                                    cursor: quizSubmitted ? "default" : "pointer",
                                                }}
                                                onClick={() => !quizSubmitted && handleSelectOption(pregunta.idPregunta, val)}
                                            >
                                                <span
                                                    style={{
                                                        display: "inline-flex",
                                                        width: 26,
                                                        height: 26,
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: "999px",
                                                        backgroundColor: "#e2e8f0",
                                                        fontSize: "0.8rem",
                                                        fontWeight: 600,
                                                        marginRight: 8,
                                                        ...letterStyle
                                                    }}
                                                >
                                                    {letter}
                                                </span>
                                                <span style={{ fontSize: "0.9rem", color: "#0f172a", fontWeight: '600' }}>{labelText}</span>

                                                {quizSubmitted && isSelected && isCorrectAnswer && (
                                                    <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#16a34a", fontWeight: 600 }}>Correcta</span>
                                                )}
                                                {quizSubmitted && isSelected && !isCorrectAnswer && (
                                                    <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#ef4444", fontWeight: 600 }}>Incorrecta</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div>
                                {(pregunta.opciones || []).map((opcion, idx) => {
                                    const llave =
                                        opcion.llave ||
                                        opcion.Llave ||
                                        String.fromCharCode(65 + idx);
                                    const desc =
                                        opcion.descripcion ||
                                        opcion.Descripcion ||
                                        opcion.texto ||
                                        opcion.Texto ||
                                        "";
                                    const esCorrecta =
                                        opcion.esCorrecta !== undefined
                                            ? opcion.esCorrecta
                                            : opcion.EsCorrecta !== undefined
                                                ? opcion.EsCorrecta
                                                : false;

                                    const idPregunta = pregunta.idPregunta;
                                    const idOpcion = opcion.idOpcion || opcion.IdOpcion;
                                    const selectedVal = respuestasUsuario[idPregunta];
                                    const isMultiple = pregunta.permiteMultiple;
                                    let isSelected = false;

                                    if (isMultiple && Array.isArray(selectedVal)) {
                                        isSelected = selectedVal.some(id => String(id) === String(idOpcion));
                                    } else {
                                        isSelected = selectedVal && String(selectedVal) === String(idOpcion);
                                    }

                                    let backgroundColor = "transparent";
                                    let border = "1px solid #e2e8f0";
                                    let letterStyle = {};

                                    // Feedback Opción Múltiple
                                    if (quizSubmitted) {
                                        if (esCorrecta) {
                                            backgroundColor = "#ecfdf3";
                                            border = "1px solid #22c55e";
                                            letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                        } else if (isSelected) {
                                            backgroundColor = "#fef2f2";
                                            border = "1px solid #ef4444";
                                            letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                        }
                                    } else if (isSelected) {
                                        // Verde al seleccionar durante el intento
                                        backgroundColor = "#ecfdf3";
                                        border = "1px solid #22c55e";
                                        letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                    }

                                    return (
                                        <div
                                            key={opcion.idOpcion || opcion.IdOpcion || idx}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "0.4rem 0.6rem",
                                                borderRadius: 6,
                                                backgroundColor,
                                                border,
                                                marginBottom: 6,
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                !quizSubmitted &&
                                                idPregunta &&
                                                idOpcion &&
                                                handleSelectOption(idPregunta, idOpcion, isMultiple)
                                            }
                                        >
                                            <span
                                                style={{
                                                    display: "inline-flex",
                                                    width: 26,
                                                    height: 26,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: "999px",
                                                    backgroundColor: "#e2e8f0",
                                                    fontSize: "0.8rem",
                                                    fontWeight: 600,
                                                    marginRight: 8,
                                                    ...letterStyle
                                                }}
                                            >
                                                {llave}
                                            </span>
                                            <span style={{ fontSize: "0.9rem", color: "#0f172a" }}>
                                                {desc}
                                            </span>
                                            {quizSubmitted && isSelected && esCorrecta && (
                                                <span
                                                    style={{
                                                        marginLeft: "auto",
                                                        fontSize: "0.75rem",
                                                        color: "#16a34a",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Correcta
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderExamView = () => {
        if (loadingQuestions) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando preguntas del examen...
                </div>
            );
        }

        if (!examPreguntas || examPreguntas.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Este examen aún no tiene preguntas configuradas.
                </div>
            );
        }

        return (
            <div style={{ marginTop: "1.5rem" }}>
                {examPreguntas.map((pregunta, index) => {
                    const descripcion =
                        pregunta.descripcionPregunta ||
                        pregunta.DescripcionPregunta ||
                        pregunta.descripcion ||
                        "";

                    return (
                        <div
                            key={pregunta.idPregunta || index}
                            style={{
                                border: "1px solid #e2e8f0",
                                borderRadius: 8,
                                padding: "1rem 1.25rem",
                                marginBottom: "1rem",
                                backgroundColor: "#ffffff",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "0.5rem",
                                }}
                            >
                                <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: 0 }}>
                                    Pregunta {index + 1}
                                    {pregunta.permiteMultiple && (
                                        <span style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: "normal", marginLeft: "8px" }}>
                                            (más de una respuesta)
                                        </span>
                                    )}
                                </h3>
                            </div>

                            {renderQuestionWithBlanks(descripcion, pregunta.idPregunta, pregunta)}

                            {/* Verdadero / Falso */}
                            {(pregunta.tipo === "verdaderoFalso" || parseInt(pregunta.idTipoPregunta || pregunta.IdTipoPregunta, 10) === 3) && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', marginBottom: '1rem' }}>
                                    {[true, false].map((val) => {
                                        const isTruth = val === true;
                                        const labelText = isTruth ? "Verdadero" : "Falso";
                                        const letter = isTruth ? "A" : "B";

                                        const userAns = respuestasUsuario[pregunta.idPregunta];
                                        const isSelected = String(userAns) === String(val);

                                        const correctAnswer = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta !== undefined
                                            ? pregunta.respuestaVerdaderoFalso?.respuestaCorrecta
                                            : pregunta.respuestaVerdaderoFalso?.RespuestaCorrecta;

                                        const isCorrectAnswer = String(correctAnswer) === String(val);

                                        let backgroundColor = "transparent";
                                        let border = "1px solid #e2e8f0";
                                        let letterStyle = {};

                                        // Feedback Examen V/F
                                        if (examSubmitted) {
                                            if (isCorrectAnswer) {
                                                backgroundColor = "#ecfdf3";
                                                border = "1px solid #22c55e";
                                                letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                            } else if (isSelected) {
                                                backgroundColor = "#fef2f2";
                                                border = "1px solid #ef4444";
                                                letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                            }
                                        } else if (isSelected) {
                                            // Verde al seleccionar durante el intento
                                            backgroundColor = "#ecfdf3";
                                            border = "1px solid #22c55e";
                                            letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                        }

                                        return (
                                            <div
                                                key={String(val)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "0.4rem 0.6rem",
                                                    borderRadius: 6,
                                                    backgroundColor,
                                                    border,
                                                    flex: 1,
                                                    cursor: examSubmitted ? "default" : "pointer",
                                                }}
                                                onClick={() => !examSubmitted && handleSelectOption(pregunta.idPregunta, val)}
                                            >
                                                <span
                                                    style={{
                                                        display: "inline-flex",
                                                        width: 26,
                                                        height: 26,
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: "999px",
                                                        backgroundColor: "#e2e8f0",
                                                        fontSize: "0.8rem",
                                                        fontWeight: 600,
                                                        marginRight: 8,
                                                        ...letterStyle
                                                    }}
                                                >
                                                    {letter}
                                                </span>
                                                <span style={{ fontSize: "0.9rem", color: "#0f172a", fontWeight: '600' }}>{labelText}</span>
                                                {examSubmitted && isCorrectAnswer && (
                                                    <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#16a34a", fontWeight: 600 }}>Correcta</span>
                                                )}
                                                {examSubmitted && isSelected && !isCorrectAnswer && (
                                                    <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#ef4444", fontWeight: 600 }}>Incorrecta</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div>
                                {(pregunta.opciones || []).map((opcion, idx) => {
                                    const llave =
                                        opcion.llave ||
                                        opcion.Llave ||
                                        String.fromCharCode(65 + idx);
                                    const desc =
                                        opcion.descripcion ||
                                        opcion.Descripcion ||
                                        opcion.texto ||
                                        opcion.Texto ||
                                        "";
                                    const esCorrecta =
                                        opcion.esCorrecta !== undefined
                                            ? opcion.esCorrecta
                                            : opcion.EsCorrecta !== undefined
                                                ? opcion.EsCorrecta
                                                : false;

                                    const idPregunta = pregunta.idPregunta;
                                    const idOpcion = opcion.idOpcion || opcion.IdOpcion;
                                    const selectedVal = respuestasUsuario[idPregunta];
                                    const isMultiple = pregunta.permiteMultiple;
                                    let isSelected = false;

                                    if (isMultiple && Array.isArray(selectedVal)) {
                                        isSelected = selectedVal.some(id => String(id) === String(idOpcion));
                                    } else {
                                        isSelected = selectedVal && String(selectedVal) === String(idOpcion);
                                    }

                                    let backgroundColor = "transparent";
                                    let border = "1px solid #e2e8f0";
                                    let letterStyle = {};

                                    // Feedback Examen Opción Múltiple
                                    if (examSubmitted) {
                                        if (esCorrecta) {
                                            backgroundColor = "#ecfdf3";
                                            border = "1px solid #22c55e";
                                            letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                        } else if (isSelected) {
                                            backgroundColor = "#fef2f2";
                                            border = "1px solid #ef4444";
                                            letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                        }
                                    } else if (isSelected) {
                                        // Verde al seleccionar durante el intento
                                        backgroundColor = "#ecfdf3";
                                        border = "1px solid #22c55e";
                                        letterStyle = { backgroundColor: '#10b981', borderColor: '#059669', color: 'white' };
                                    }

                                    return (
                                        <div
                                            key={opcion.idOpcion || opcion.IdOpcion || idx}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "0.4rem 0.6rem",
                                                borderRadius: 6,
                                                backgroundColor,
                                                border,
                                                marginBottom: 6,
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                !examSubmitted &&
                                                idPregunta &&
                                                idOpcion &&
                                                handleSelectOption(idPregunta, idOpcion, isMultiple)
                                            }
                                        >
                                            <span
                                                style={{
                                                    display: "inline-flex",
                                                    width: 26,
                                                    height: 26,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: "999px",
                                                    backgroundColor: "#e2e8f0",
                                                    fontSize: "0.8rem",
                                                    fontWeight: 600,
                                                    marginRight: 8,
                                                    ...letterStyle
                                                }}
                                            >
                                                {llave}
                                            </span>
                                            <span style={{ fontSize: "0.9rem", color: "#0f172a" }}>
                                                {desc}
                                            </span>
                                            {examSubmitted && esCorrecta && (
                                                <span
                                                    style={{
                                                        marginLeft: "auto",
                                                        fontSize: "0.75rem",
                                                        color: "#16a34a",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Correcta
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const getIconForType = (tipo) => {
        switch (tipo) {
            case "LECCION":
                return FiPlayCircle;
            case "CUESTIONARIO":
                return FiHelpCircle;
            case "EXAMEN":
                return FiClipboard;
            default:
                return FiPlayCircle;
        }
    };


    // ---------- RENDER ----------

    return (
        <div className="cp-page">
            {/* TOPBAR */}
            <header className="cp-topbar">
                <button
                    type="button"
                    className="cp-topbar-close"
                    onClick={() => navigate(`/menu/lms/curso/${slug}`)}
                    title="Volver al currículum"
                >
                    <FiChevronLeft />
                </button>

                <div className="cp-topbar-title">
                    <span className="cp-topbar-course">
                        {course?.titulo || course?.nombre || "Curso"} (Vista previa)
                    </span>
                </div>

                <div className="cp-topbar-close cp-topbar-close-right" />
            </header>

            <div
                className={
                    "cp-layout" + (sidebarOpen ? "" : " cp-layout--collapsed")
                }
            >
                {/* SIDEBAR */}
                <div className="cp-sidebar-wrapper">
                    <aside className="cp-sidebar">
                        <div className="cp-sidebar-list">
                            {modulesWithLessons.map((mod) => (
                                <div key={mod.moduleId} className="cp-sidebar-module">
                                    <div className="cp-sidebar-module-header">
                                        <span className="cp-sidebar-module-title">
                                            {mod.moduleTitle}
                                        </span>
                                        <span className="cp-sidebar-module-count">
                                            {mod.items.length}
                                        </span>
                                    </div>

                                    <div className="cp-sidebar-module-lessons">
                                        {mod.items.map((item) => {
                                            const isActive =
                                                (item.tipo === "LECCION" &&
                                                    String(item.lessonId) === String(currentLessonId)) ||
                                                (item.tipo !== "LECCION" &&
                                                    currentContentType === item.tipo &&
                                                    String(currentContentId) === String(item.id));

                                            const Icon = getIconForType(item.tipo);
                                            const isClickable = true;

                                            return (
                                                <button
                                                    key={`${item.tipo}-${item.id}`}
                                                    type="button"
                                                    className={
                                                        "cp-sidebar-lesson" +
                                                        (isActive ? " active" : "") +
                                                        (item.completado ? " completed" : "") +
                                                        (!isClickable ? " disabled" : "")
                                                    }
                                                    onClick={() =>
                                                        isClickable && handleClickContent(item)
                                                    }
                                                    disabled={!isClickable}
                                                    style={{
                                                        cursor: isClickable ? "pointer" : "default",
                                                        opacity: isClickable ? 1 : 0.7,
                                                    }}
                                                >
                                                    <span className="cp-sidebar-lesson-icon">
                                                        {item.completado && item.tipo === "LECCION" ? (
                                                            <FiCheckCircle />
                                                        ) : (
                                                            <Icon />
                                                        )}
                                                    </span>
                                                    <span className="cp-sidebar-lesson-title">
                                                        {item.lessonTitle}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            {modulesWithLessons.length === 0 && (
                                <p className="cp-sidebar-empty">
                                    Este curso aún no tiene lecciones configuradas.
                                </p>
                            )}
                        </div>
                    </aside>

                    <button
                        type="button"
                        className={
                            "cp-sidebar-toggle" +
                            (sidebarOpen ? "" : " cp-sidebar-toggle--collapsed")
                        }
                        onClick={() => setSidebarOpen((prev) => !prev)}
                        aria-label={
                            sidebarOpen
                                ? "Ocultar lista de lecciones"
                                : "Mostrar lista de lecciones"
                        }
                    >
                        <span className="cp-sidebar-toggle-icon">
                            {sidebarOpen ? "‹" : "›"}
                        </span>
                    </button>
                </div>

                {/* MAIN */}
                <main className="cp-main">
                    {justCompletedTitle && (
                        <div className="cp-alert-success">
                            ¡Enhorabuena! Has completado «{justCompletedTitle}».
                        </div>
                    )}

                    {currentContentType === "CUESTIONARIO" ? (
                        <>
                            <h1 className="cp-lesson-title">
                                {currentContentTitle || "Cuestionario"}
                            </h1>
                            <p className="cp-lesson-subtitle">
                                Preguntas de evaluación del curso (Vista previa)
                            </p>
                            <div
                                style={{
                                    marginTop: "1.5rem",
                                    marginBottom: "2rem",
                                    boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                    borderRadius: "8px",
                                    padding: "1rem 1.25rem",
                                    backgroundColor: "#f8fafc",
                                }}
                            >
                                {renderQuizView()}
                            </div>
                            <div className="cp-lesson-actions">
                                <button
                                    type="button"
                                    className="cp-nav-btn"
                                    onClick={handleQuizButtonClick}
                                    disabled={quizPreguntas.length === 0}
                                >
                                    {!quizSubmitted ? 'Enviar Cuestionario' : 'Siguiente'} <FiChevronRight />
                                </button>
                            </div>
                        </>
                    ) : currentContentType === "EXAMEN" ? (
                        <>
                            <h1 className="cp-lesson-title">
                                {currentContentTitle || "Examen"}
                            </h1>
                            <p className="cp-lesson-subtitle">
                                Preguntas del examen del curso (Vista previa)
                            </p>
                            <div
                                style={{
                                    marginTop: "1.5rem",
                                    marginBottom: "2rem",
                                    boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                    borderRadius: "8px",
                                    padding: "1rem 1.25rem",
                                    backgroundColor: "#f8fafc",
                                }}
                            >
                                {renderExamView()}
                            </div>
                            <div className="cp-lesson-actions">
                                <button
                                    type="button"
                                    className="cp-nav-btn"
                                    onClick={handleExamButtonClick}
                                    disabled={examPreguntas.length === 0}
                                >
                                    {!examSubmitted ? 'Enviar Examen' : 'Siguiente'} <FiChevronRight />
                                </button>
                            </div>
                        </>
                    ) : lessonSections && lessonSections.length > 0 ? (
                        <>
                            <h1 className="cp-lesson-title">
                                {currentItem?.lessonTitle || "Lección"}
                            </h1>

                            {currentItem && (
                                <p className="cp-lesson-subtitle">
                                    {currentItem.moduleTitle} · {currentIndex + 1} de{" "}
                                    {totalElements} elementos
                                </p>
                            )}

                            {/* Contenido real de la lección desde BD */}
                            <div style={{
                                marginTop: "1.5rem",
                                marginBottom: "2rem",
                                boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                borderRadius: "8px",
                                overflow: "hidden"
                            }}>
                                {renderLessonSections()}
                            </div>

                            {currentItem && (
                                <>
                                    <div className="cp-lesson-nav">
                                        <button
                                            type="button"
                                            className="cp-nav-btn"
                                            disabled={currentIndex <= 0}
                                            onClick={goPrev}
                                        >
                                            <FiChevronLeft /> Anterior
                                        </button>
                                        <button
                                            type="button"
                                            className="cp-nav-btn"
                                            onClick={goNext}
                                        >
                                            Siguiente <FiChevronRight />
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="cp-empty-main">
                            <p>No hay contenido seleccionado en este curso todavía.</p>
                        </div>
                    )
                    }
                </main >
                {showResultsModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        backdropFilter: 'blur(4px)'
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            padding: '2rem',
                            width: '90%',
                            maxWidth: '400px',
                            textAlign: 'center',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', marginBottom: '1.5rem' }}>
                                Resultados
                            </h2>

                            <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '2rem' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#22c55e' }}>
                                        {resultsData.correctas}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Correctas</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }}>
                                        {resultsData.incorrectas}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Incorrectas</div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '2rem' }}>
                                <div style={{
                                    fontSize: '4rem',
                                    fontWeight: '800',
                                    color: resultsData.aprobado ? '#22c55e' : '#ef4444',
                                    lineHeight: 1
                                }}>
                                    {resultsData.calificacion}
                                </div>
                                <div style={{
                                    fontSize: '1.25rem',
                                    fontWeight: '600',
                                    color: resultsData.aprobado ? '#22c55e' : '#ef4444',
                                    marginTop: '0.5rem'
                                }}>
                                    {resultsData.aprobado ? '¡Aprobado!' : 'No aprobado'}
                                </div>
                            </div>

                            <button
                                onClick={() => setShowResultsModal(false)}
                                style={{
                                    backgroundColor: '#0f172a',
                                    color: 'white',
                                    padding: '0.75rem 2rem',
                                    borderRadius: '8px',
                                    border: 'none',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    width: '100%',
                                    fontSize: '1rem',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#1e293b'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#0f172a'}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                )}
            </div >
            <ObservacionesModal
                isOpen={isObservacionesModalOpen}
                onClose={() => setIsObservacionesModalOpen(false)}
                idCurso={slug}
                currentContentId={currentContentId}
                currentContentType={currentContentType}
                readOnly={true} // El instructor solo ve las observaciones
            />
            {/* Floating Button for Observations */}
            <button
                className="floating-observaciones-btn"
                onClick={() => setIsObservacionesModalOpen(true)}
                title="Historial de Observaciones"
                style={{
                    position: 'fixed',
                    bottom: '30px',
                    right: '30px',
                    backgroundColor: 'var(--color-primary, #2563eb)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    fontSize: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    zIndex: 1000
                }}
            >
                <FiMessageSquare />
            </button>
        </div >
    );
};

export default CoursePlayerPreview;
